import { useMemo } from 'react';
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';

import type { Participant } from '../../../interfaces/Participant';
import useBoardStore from '../../../store/boardStore';
import useUserStore from '../../../store/userStore';

type ParticipantsPanelProps = {
  participants: Participant[];
};

const getParticipantSortKey = (name: string) => name.trim().toLowerCase();

const ParticipantsPanel = ({ participants }: ParticipantsPanelProps) => {
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const removeParticipant = useBoardStore((state) => state.removeParticipant);
  const currentUser = useUserStore((state) => state.getCurrentUser());

  const uniqueParticipants = useMemo(() => {
    const participantsByName = new Map<string, Participant>();

    participants.forEach((participant) => {
      const trimmedName = participant.name.trim();
      if (!trimmedName) {
        return;
      }

      const nameKey = getParticipantSortKey(trimmedName);
      const existingParticipant = participantsByName.get(nameKey);

      if (!existingParticipant) {
        participantsByName.set(nameKey, participant);
        return;
      }

      if (currentUser && participant.id === currentUser.id) {
        participantsByName.set(nameKey, participant);
      }
    });

    return Array.from(participantsByName.values()).sort((left, right) => {
      const leftIsCurrentUser = currentUser && left.id === currentUser.id;
      const rightIsCurrentUser = currentUser && right.id === currentUser.id;

      if (leftIsCurrentUser && !rightIsCurrentUser) {
        return -1;
      }

      if (!leftIsCurrentUser && rightIsCurrentUser) {
        return 1;
      }

      return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
    });
  }, [participants, currentUser]);

  const canDeleteParticipants = boardDetails?.isOwner === true;

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        height: 'fit-content',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: '#ffd04b',
        }}
      >
        <Typography variant="h6">Participants</Typography>
      </Box>
      <Stack
        spacing={1}
        sx={{
          pl: 2,
          pr: 1,
          py: 1.5,
          maxHeight: { xs: 220, md: 'calc(100vh - 190px)' },
          overflowY: 'auto',
        }}
      >
        {uniqueParticipants.map((participant) => {
          const canDeleteParticipant =
            canDeleteParticipants && currentUser && participant.id !== currentUser.id;

          return (
            <Stack
              key={participant.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2">{participant.name}</Typography>
              {canDeleteParticipant ? (
                <Tooltip title="Remove participant">
                  <IconButton
                    aria-label={`Remove ${participant.name}`}
                    size="small"
                    color="error"
                    onClick={() => {
                      void removeParticipant(participant);
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          );
        })}
        {uniqueParticipants.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No participants yet
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default ParticipantsPanel;
